import React from 'react';
import {
  Wrench,
  LayoutDashboard,
  ClipboardCheck,
  ShieldCheck,
  Settings as SettingsIcon,
} from 'lucide-react';
import { OfflineStatusBadge } from './OfflineStatusBadge';
import type { NavigationTab } from '../../types';

export interface NavbarProps {
  activeTab: NavigationTab;
  onSelectTab: (tab: NavigationTab) => void;
  selectedMachineName?: string;
  onManualSync?: () => void;
}

export const Navbar: React.FC<NavbarProps> = ({
  activeTab,
  onSelectTab,
  selectedMachineName,
  onManualSync,
}) => {
  const navItems: Array<{ id: NavigationTab; label: string; icon: React.ComponentType<{ className?: string }> }> = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'inspection', label: 'Inspection', icon: ClipboardCheck },
    { id: 'admin', label: 'Admin', icon: ShieldCheck },
    { id: 'settings', label: 'Settings', icon: SettingsIcon },
  ];

  return (
    <header className="sticky top-0 z-40 w-full bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-2xs">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Brand Logo & Name */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center text-white shadow-md shadow-blue-600/20">
              <Wrench className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-extrabold text-base sm:text-lg tracking-tight text-slate-900">
                  CNC <span className="text-blue-600">PM</span>
                </span>
                <span className="hidden sm:inline-block px-2 py-0.5 text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200 rounded uppercase">
                  Workshop PWA
                </span>
              </div>
              {selectedMachineName ? (
                <p className="text-xs text-slate-500 font-medium truncate max-w-[140px] sm:max-w-xs">
                  {selectedMachineName}
                </p>
              ) : (
                <p className="text-[11px] text-slate-500 hidden sm:block">
                  Preventative Maintenance System
                </p>
              )}
            </div>
          </div>

          {/* Desktop / Tablet Nav Tabs */}
          <nav className="hidden md:flex items-center space-x-1 lg:space-x-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => onSelectTab(item.id)}
                  className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-semibold transition-all min-h-[44px] cursor-pointer ${
                    isActive
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                  }`}
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>

          {/* Right Action: Sync Status Badge */}
          <div className="flex items-center gap-2 sm:gap-3">
            <OfflineStatusBadge onManualSync={onManualSync} />
          </div>
        </div>
      </div>
    </header>
  );
};
